/**
 * Per-site mesh topology cache.
 *
 * Backed by /api/topology/site/{slug}/mesh/. Only fetched when the
 * /topology page narrows to a single site filter — at that point the
 * mesh-aware layout becomes useful. We cache results per-slug in-memory
 * so toggling filters doesn't re-fetch.
 *
 * Errors fall back to an empty mesh shape so callers can still render
 * the class-grouped fallback layout instead of crashing.
 */
import { create } from "zustand";
import { get as apiGet, ApiError } from "@/api/client";

export interface MeshTopology {
  /** AP serials with an empty meshRoute — these uplink via Ethernet. */
  roots: string[];
  /** Direct parent for each non-root AP. parentOf[childSerial] = parentSerial. */
  parentOf: Record<string, string>;
  /** Hop distance from a wired root. 0 = root, 1+ = mesh leaf. */
  hopCount: Record<string, number>;
  /** Reverse index: parent serial → list of immediate child serials. */
  children: Record<string, string[]>;
}

const EMPTY_MESH: MeshTopology = {
  roots: [],
  parentOf: {},
  hopCount: {},
  children: {},
};

interface State {
  /** slug → topology. Negative cache (failed lookup) also stored as
   *  EMPTY_MESH so we don't hammer a failing endpoint. */
  bySlug: Record<string, MeshTopology>;
  /** slug → "loading" while a fetch is in flight, prevents thundering herd. */
  loading: Record<string, true>;

  load: (slug: string) => Promise<MeshTopology>;
}

export const useTopologyMesh = create<State>((set, get) => ({
  bySlug: {},
  loading: {},

  load: async (slug: string) => {
    const key = slug.toLowerCase();
    const cached = get().bySlug[key];
    if (cached) return cached;
    if (get().loading[key]) {
      // Another caller is already fetching — wait for it via a polling
      // promise loop. Cheap because the in-flight fetch typically
      // completes in <500ms.
      return await new Promise<MeshTopology>((resolve) => {
        const t = setInterval(() => {
          const s = get();
          if (s.bySlug[key]) {
            clearInterval(t);
            resolve(s.bySlug[key]);
          }
        }, 80);
      });
    }
    set((s) => ({ loading: { ...s.loading, [key]: true } }));
    try {
      const raw = await apiGet<MeshTopology>(`/topology/site/${encodeURIComponent(key)}/mesh/`);
      // Defensive: backend should always return the four keys, but if
      // the proxy mangles the response shape we'd rather fall back to
      // empty than crash the topology page.
      const safe: MeshTopology = {
        roots:    Array.isArray(raw?.roots)     ? raw.roots     : [],
        parentOf: raw?.parentOf && typeof raw.parentOf === "object" ? raw.parentOf : {},
        hopCount: raw?.hopCount && typeof raw.hopCount === "object" ? raw.hopCount : {},
        children: raw?.children && typeof raw.children === "object" ? raw.children : {},
      };
      set((s) => ({
        bySlug:  { ...s.bySlug,  [key]: safe },
        loading: { ...s.loading, [key]: undefined as unknown as true },
      }));
      return safe;
    } catch (err) {
      // 404 = site has no wireless APs OR the slug doesn't match a network
      // we can see. Either way, store empty so we stop retrying.
      if (!(err instanceof ApiError) || err.status >= 500) {
        // 5xx is transient; clear loading so a later retry can fire.
        set((s) => ({ loading: { ...s.loading, [key]: undefined as unknown as true } }));
      } else {
        set((s) => ({
          bySlug:  { ...s.bySlug,  [key]: EMPTY_MESH },
          loading: { ...s.loading, [key]: undefined as unknown as true },
        }));
      }
      return EMPTY_MESH;
    }
  },
}));
